import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useBooking } from '@features/booking/state';
import { getStepIdFromPath } from '@features/booking/flow/steps';

/**
 * Keeps Sentry scope tags in sync with the current booking step and flow type.
 *
 * These tags show up as filterable fields on every captured event, so issues
 * can be sliced by `booking.step` and `booking.flow_type` in the Sentry UI.
 *
 * Tags are cleared when the user navigates to a route outside the booking flow
 * (e.g. /profile, /gift-card) so non-booking errors are not attributed to
 * stale booking state.
 *
 * Must be mounted inside BookingProvider.
 */
export function BookingScopeSetter() {
  const location = useLocation();
  const { flowType, isEarlyAccessFlow } = useBooking();

  useEffect(() => {
    const stepId = getStepIdFromPath(location.pathname);
    if (stepId) {
      Sentry.setTag('booking.step', stepId);
      Sentry.setTag('booking.flow_type', flowType || 'standard');
      Sentry.setTag('booking.early_access', isEarlyAccessFlow);
    } else {
      Sentry.setTag('booking.step', undefined);
      Sentry.setTag('booking.flow_type', undefined);
      Sentry.setTag('booking.early_access', undefined);
    }
  }, [location.pathname, flowType, isEarlyAccessFlow]);

  useEffect(() => {
    return () => {
      Sentry.setTag('booking.step', undefined);
      Sentry.setTag('booking.flow_type', undefined);
      Sentry.setTag('booking.early_access', undefined);
    };
  }, []);

  return null;
}
